import clsx from "clsx";

import { DateTimeTz, Icon } from "@web-app/components";
import { OrderTimeline } from "@web-app/types";
import { OrderTimelineSchema, timeZoneMap } from "@web-app/utils/helpers";

type ChecklistItemActivityProps = {
  item: OrderTimeline;
  showTimelineBar: boolean;
  timezone: keyof typeof timeZoneMap | undefined;
};

type ChecklistValue = { label: string; quantity?: number };

// CHECK/UNCHECK store the label as plain text; CREATE/DELETE/UPDATE store
// {label, quantity} as JSON.
const parseValue = (value: string): ChecklistValue => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed && typeof parsed === "object" && "label" in parsed) {
      return parsed as ChecklistValue;
    }
  } catch {
    // plain label
  }
  return { label: value };
};

const withQuantity = ({ label, quantity }: ChecklistValue) =>
  `"${label}"${quantity ? ` x ${quantity}` : ""}`;

const describe = (item: OrderTimeline) => {
  const previous = parseValue(item.previousValue);
  const updated = parseValue(item.updatedValue);

  switch (item.action) {
    case "CREATE":
      return withQuantity(updated);
    case "DELETE":
      return withQuantity(previous);
    case "UPDATE":
      return `"${updated.label}" quantity from ${previous.quantity ?? "none"} to ${updated.quantity ?? "none"}`;
    default:
      return `"${updated.label}"`;
  }
};

export default function ChecklistItemActivity({
  item,
  showTimelineBar,
  timezone,
}: ChecklistItemActivityProps) {
  const schema = OrderTimelineSchema[item.entity];
  const action = schema?.action[item.action];

  if (!action) {
    return null;
  }

  return (
    <li className="relative flex gap-x-2 md:gap-x-4">
      <div
        className={clsx(
          showTimelineBar ? "-bottom-6" : "h-6",
          "absolute left-0 top-0 flex w-6 justify-center",
        )}
      >
        <div className="w-px bg-gray-200" />
      </div>
      <div className="relative flex h-7 w-6 flex-none items-center justify-center bg-white">
        <Icon name={action.icon} className={clsx("h-3 md:h-4", action.color)} />
      </div>
      <p className="flex-auto flex flex-col items-start py-0.5 text-[10px] md:text-xs leading-0 md:leading-5 text-gray-500">
        <span
          className={clsx(
            "inline-block font-medium text-gray-900",
            action.color,
          )}
        >
          {`${action.text} ${schema.entityPresentation} ${describe(item)}`}
        </span>
        <span className="relative">
          <DateTimeTz
            start={new Date(item?.createdAt)}
            timezone={timezone}
            createdBy={item?.createdBy?.name}
            presentation="timeline"
          />
        </span>
      </p>
    </li>
  );
}
